import dayjs from 'dayjs';
import { Duration } from 'dayjs/plugin/duration.js';
import { kMillisecondsPerMinute } from '../../utility/constants.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/dto.js';
import { TimeZoneRegion } from './time-zone-region.js';
import { TimeZone } from '../../time/timezone.js';

export class DateSpan extends DataModel {
  static readonly TYPE_ID: string = '4d9da12e3f9d4ce0b3f4136efafd09ec';

  constructor(
    public readonly from: dayjs.Dayjs,
    public readonly to: dayjs.Dayjs,
  ) {
    super();
  }

  get $typeId(): string {
    return DateSpan.TYPE_ID;
  }

  get isExact(): boolean {
    return this.from.isSame(this.to);
  }

  duration(): Duration {
    const utcOffsetOfFirst = this.from.utcOffset();
    const utcOffsetOfSecond = this.to.utcOffset();

    if(utcOffsetOfFirst === utcOffsetOfSecond) {
      return dayjs.duration(this.from.diff(this.to));
    }

    const timeZoneDiff = utcOffsetOfFirst - utcOffsetOfSecond;
    const timeDiff = this.from.diff(this.to, 'milliseconds') + (timeZoneDiff * kMillisecondsPerMinute);

    return dayjs.duration(timeDiff, 'milliseconds');
  }

  toFormattedString(): string {
    return this.from.isSame(this.to) ?
      this.from.format('DD.MM'):
      `${this.from.format('DD.MM')}-${this.to.format('DD.MM')}`;
  }

  static fromMap(context: SerializationContext, map: SerializedData): DateSpan {
    return new DateSpan(
        context.decodeDate(MapUtils.get<number>(map, 'from')),
        context.decodeDate(MapUtils.get<number>(map, 'to')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'from': context.encodeDate(this.from),
      'to': context.encodeDate(this.to),
    };
  }

  clone(): this {
    return new DateSpan(
        this.from,
        this.to,
    ) as this;
  }

  isSame(other: DateSpan): boolean {
    if(other === this) {
      return true;
    }

    return other.from === this.from &&
          other.to === this.to;
  }

  atTimeZone(timezone: TimeZoneRegion): DateSpan {
    return new DateSpan(
        TimeZone.ofRegion(timezone).convert(this.from),
        TimeZone.ofRegion(timezone).convert(this.to),
    );
  }

  contains(date: dayjs.Dayjs): boolean {
    return date.isAfter(this.from) && date.isBefore(this.to);
  }

  includes(other: DateSpan): boolean {
    return this.from.isBefore(other.from) && this.to.isAfter(other.to);
  }

  intersects(other: DateSpan): boolean {
    return this.contains(other.from) || this.contains(other.to) || other.includes(this);
  }
}

export class DateSpanBuilder extends DataModelBuilder<DateSpan> {
  build(context: SerializationContext, map: SerializedData): DateSpan {
    return DateSpan.fromMap(context, map);
  }
}
